import { jwtDecode } from "jwt-decode";

const TOKEN_KEY = "token";
const PERMISSIONS_KEY = "user_permissions";

export const getToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

export const setToken = (token) => {
  if (!token) return;
  localStorage.setItem(TOKEN_KEY, token);
  window.dispatchEvent(new Event("token-changed"));
};

export const getUserPermissions = () => {
  try {
    const raw = localStorage.getItem(PERMISSIONS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export const setUserPermissions = (permissions) => {
  try {
    localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(permissions || []));
  } catch {
    localStorage.setItem(PERMISSIONS_KEY, "[]");
  }
  window.dispatchEvent(new Event("token-changed"));
};

export const clearUserPermissions = () => {
  localStorage.removeItem(PERMISSIONS_KEY);
  window.dispatchEvent(new Event("token-changed"));
};

export const logout = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(PERMISSIONS_KEY);
  localStorage.removeItem("session-exp-ms");
  window.dispatchEvent(new Event("token-changed"));
};

export const isTokenExpired = (token) => {
  try {
    const decoded = jwtDecode(token);
    return decoded.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

export const isAuthenticated = () => {
  const token = getToken();
  return token !== null && !isTokenExpired(token);
};

export const getUserRole = () => {
  const token = getToken();
  if (!token) return null;

  try {
    const decoded = jwtDecode(token);
    return decoded.role_name || decoded.role || null;
  } catch {
    return null;
  }
};

export const getDecodedToken = () => {
  const token = getToken();
  if (!token) return null;

  try {
    return jwtDecode(token);
  } catch {
    return null;
  }
};

export const getUserId = () => {
  const decoded = getDecodedToken();
  return decoded?.id ?? null;
};

export const getTenantId = () => {
  const decoded = getDecodedToken();
  return decoded?.tenant_id ?? null;
};

export const getPermissionsVersion = () => {
  const decoded = getDecodedToken();
  return decoded?.permissions_version ?? 1;
};

export const isPermissionsChangedError = (errorLike) => {
  const message =
    typeof errorLike === "string"
      ? errorLike
      : errorLike?.message || errorLike?.detail || "";

  return String(message).toLowerCase().includes("permissions changed");
};

export const isRoleChangedError = (errorLike) => {
  const message =
    typeof errorLike === "string"
      ? errorLike
      : errorLike?.message || errorLike?.detail || "";

  return String(message).toLowerCase().includes("role changed");
};

export const refreshAccessToken = async (apiBaseUrl) => {
  const token = getToken();

  const headers = token
    ? {
        Authorization: `Bearer ${token}`,
      }
    : {};

  const response = await fetch(`${apiBaseUrl}/auth/refresh`, {
    method: "POST",
    headers,
    credentials: "include",
  });

  if (!response.ok) {
    let detail = "Failed to refresh session";

    try {
      const data = await response.json();
      detail = data?.detail || detail;
    } catch {
      // ignore
    }

    throw new Error(detail);
  }

  const data = await response.json();
  const nextToken = data?.access_token;

  if (!nextToken) {
    throw new Error("Refresh succeeded but token is missing");
  }

  setToken(nextToken);
  return nextToken;
};

export const loadMyPermissions = async (apiBaseUrl, tokenOverride = null) => {
  const token = tokenOverride || getToken();

  const headers = token
    ? {
        Authorization: `Bearer ${token}`,
      }
    : {};

  const response = await fetch(`${apiBaseUrl}/auth/me/permissions`, {
    method: "GET",
    headers,
    credentials: "include",
  });

  if (!response.ok) {
    let detail = "Failed to load permissions";

    try {
      const data = await response.json();
      detail = data?.detail || detail;
    } catch {
      // ignore
    }

    throw new Error(detail);
  }

  const permissions = await response.json();
  const normalized = Array.isArray(permissions) ? permissions : [];
  setUserPermissions(normalized);
  return normalized;
};

export const refreshSessionAndPermissions = async (apiBaseUrl) => {
  const nextToken = await refreshAccessToken(apiBaseUrl);
  const permissions = await loadMyPermissions(apiBaseUrl, nextToken);

  window.dispatchEvent(
    new CustomEvent("permissions-updated", {
      detail: {
        token: nextToken,
        permissions,
      },
    })
  );

  return {
    token: nextToken,
    permissions,
  };
};